import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { IconButton, Portal, Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import styles from './AssetModalShell.module.scss';

const DRAG_HANDLE_CLASS = 'asset-modal-drag-handle';

/**
 * Xtram modal shell for the Assets page (JournalEntryTemplatesModal look): blurred overlay,
 * orange rail, blue title, scrolling body and a footer action row.
 * The overlay sits just below the shared EditModal (z-index 1300) so documents opened
 * from an asset modal still appear on top.
 */
const AssetModalShell = ({
    open = false,
    onClose,
    title = null,
    subtitle = null,
    direction = 'rtl',
    size = 'md',
    fullHeight = false,
    headerActions = null,
    toolbar = null,
    footer = null,
    footerStart = null,
    closeOnBackdrop = false,
    draggable = true,
    scrollBody = true,
    zIndex,
    children = null,
}) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);
    const pressedOnBackdrop = useRef(false);

    if (!open) return null;

    const modalClassName = [
        styles.modal,
        styles[size] || styles.md,
        fullHeight ? styles.fullHeight : '',
    ].filter(Boolean).join(' ');

    const box = (
        <div ref={nodeRef} className={modalClassName} role="dialog" aria-modal="true" dir={direction}>
            <div className={styles.body}>
                <div className={`${styles.header} ${draggable ? `${styles.draggable} ${DRAG_HANDLE_CLASS}` : ''}`}>
                    <Tooltip title={t('Close') || 'Close'}>
                        <IconButton onClick={onClose} className={styles.closeIcon}>
                            <CancelIcon className={styles.closeIcon} />
                        </IconButton>
                    </Tooltip>
                    <div className={styles.headerTitle}>
                        <span>{title}</span>
                        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
                    </div>
                    <div className={styles.headerSide}>{headerActions}</div>
                </div>

                {toolbar && <div className={styles.toolbarSlot}>{toolbar}</div>}

                <div className={scrollBody ? styles.scroll : styles.fill}>{children}</div>

                {(footer || footerStart) && (
                    <div className={styles.footer}>
                        {footerStart && <div className={styles.footerStart}>{footerStart}</div>}
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                style={zIndex ? { zIndex } : undefined}
                onMouseDown={(e) => { pressedOnBackdrop.current = e.target === e.currentTarget; }}
                onClick={(e) => {
                    if (closeOnBackdrop && pressedOnBackdrop.current && e.target === e.currentTarget) onClose();
                }}
            >
                {draggable ? (
                    <Draggable
                        handle={`.${DRAG_HANDLE_CLASS}`}
                        cancel="button, input, .MuiIconButton-root"
                        nodeRef={nodeRef}
                    >
                        {box}
                    </Draggable>
                ) : box}
            </div>
        </Portal>
    );
};

AssetModalShell.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node,
    subtitle: PropTypes.node,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
    size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
    fullHeight: PropTypes.bool,
    headerActions: PropTypes.node,
    toolbar: PropTypes.node,
    footer: PropTypes.node,
    footerStart: PropTypes.node,
    closeOnBackdrop: PropTypes.bool,
    draggable: PropTypes.bool,
    /** False when the content manages its own scrolling (e.g. a full-height report). */
    scrollBody: PropTypes.bool,
    zIndex: PropTypes.number,
    children: PropTypes.node,
};

export default AssetModalShell;
